import { registry as cookieRegistry } from './cookie-consent-registry.js';
import { registry as integrationRegistry } from './integration-consent-registry.js';
import { getCookie } from './utils.js';
const stubFn = () => { };
export class ConsentRegistry {
    static integrationRegistry = integrationRegistry;
    static cookieRegistry = cookieRegistry;
    /**
     * Ensure that all required consents for using `integrationName` are granted,
     * and subscribe a listener for when / if that status changes.
     *
     * ## Example
     *
     * ```typescript
     * const [ iterateConsentGranted, unsubIterateConsent ] =
     *   await ConsentRegistry.useIntegration("Iterate", () => {
     *     // example pseudo code to call if consent is revoked:
     *     iterate.disable();
     *   });
     * ```
     *
     * Note: calling the cleanup function `unsubIterateConsent` has no effect if
     * consent wasn't granted.
     *
     * @param onRevoked - Callback invoked if any of the required consents are
     *                    later revoked.
     * @returns A promise that resolves with a boolean status, and a cleanup
     * function. Invoking the cleanup function will unsubscribe the `onRevoked`
     * callback.
     */
    static async useIntegration(integrationName, onRevoked) {
        const categories = await this.integrationRegistry.getConsentCategories(integrationName);
        const [granted, cleanup] = await ConsentRegistry.useMultiConsent(categories, onRevoked);
        return granted ? [true, cleanup] : [false, stubFn];
    }
    /**
     * Ensure that all required consents for using the given cookie are granted,
     * and subscribe a listener for when / if that status changes.
     *
     * ## Example
     *
     * ```typescript
     * const [ fooConsentGranted, unsubFooConsent, fooValue ] =
     *   await ConsentRegistry.useCookie("foo", () => {
     *     // example pseudo code to call if consent is revoked:
     *     CookieStorage.remove("foo");
     *   });
     * ```
     *
     * Note: calling the cleanup function `unsubFooConsent` has no effect if
     * consent wasn't granted.
     *
     * @param onRevoked - Callback invoked if any of the required consents are
     *                    later revoked.
     * @returns A promise that resolves with a boolean status, a cleanup
     * function, and the cookie value if consent was granted. Invoking the
     * cleanup function will unsubscribe the `onRevoked` callback.
     */
    static async useCookie(cookieName, onRevoked) {
        const categories = await this.cookieRegistry.getConsentCategories(cookieName);
        const [granted, cleanup] = await ConsentRegistry.useMultiConsent(categories, onRevoked);
        return granted
            ? [true, cleanup, getCookie(cookieName)]
            : [false, stubFn, undefined];
    }
    /**
     * Ensure that a given consent is granted, and subscribe a listener for when /
     * if that status changes.
     *
     * @param onRevoked - Callback invoked if the consent is later revoked.
     * @returns A promise that resolves with a boolean status, and a cleanup
     * function. Invoking the cleanup function will unsubscribe the `onRevoked`
     * callback.
     */
    static async useConsent(category, onRevoked) {
        const tcfManager = await this.getConsentManager();
        const result = (await Promise.allSettled([
            new Promise((resolve, reject) => {
                tcfManager.getPermission(category, (consentState) => {
                    consentState === '1' ? resolve() : reject();
                });
            }),
        ]))[0];
        if (result.status === 'rejected') {
            return [false, stubFn];
        }
        const cleanup = tcfManager.subscribe(category, (state) => {
            if (state !== '1')
                onRevoked();
        });
        return [true, cleanup];
    }
    /**
     * Ensure that all given consent categories are granted, and subscribe a
     * listener for when / if that status changes.
     *
     * @param onRevoked - Callback invoked if any of the required consents are
     *                    later revoked.
     * @returns A promise that resolves with a boolean status, and a cleanup
     * function. Invoking the cleanup function will unsubscribe the `onRevoked`
     * callback.
     */
    static async useMultiConsent(categories, onRevoked) {
        const results = await Promise.allSettled(categories.map((category) => ConsentRegistry.useConsent(category, onRevoked)));
        const [cleanupFns, failed] = results.reduce((acc, result) => {
            if (result.status === 'rejected')
                throw new Error('Unexpected');
            if (result.value[0])
                acc[0].push(result.value[1]);
            else
                acc[1]++;
            return acc;
        }, [[], 0]);
        const cleanup = () => {
            for (const fn of cleanupFns)
                fn();
        };
        if (failed > 0) {
            cleanup();
            return [false, stubFn];
        }
        return [true, cleanup];
    }
    /**
     * Returns the consent manager (`_tcf_`), if available.
     *
     * It will await "cmp:ready" event if the consent manager is not available
     * yet and return it when it is.
     *
     * @returns The consent manager object.
     */
    static async getConsentManager() {
        const getManager = () => window._tcf_;
        return new Promise((resolve) => {
            const manager = getManager();
            if (manager)
                return resolve(manager);
            // TODO: change to `cmp:ready` instead when consent manager is updated.
            document.addEventListener('cmp:loaded', () => resolve(getManager()), {
                once: true,
            });
        });
    }
}
